// Wischgeste zum Löschen – der Standard, den jede Listen-App auf dem
// Telefon mitbringt. Nach links ziehen legt eine rote Fläche frei; wer weit
// genug zieht, löst aus. Rückgängig macht der Toast.
import { el, haptic } from './dom.js';
import { icon } from './icons.js';

export interface SwipeOptions {
  onDelete: () => void;
  /** Ab wie vielen Pixeln gilt es als Auslösen? */
  threshold?: number;
  actionLabel?: string;
}

/**
 * Legt `content` in einen Wisch-Container. Der Rückgabewert ist das Element,
 * das in die Liste gehängt wird.
 */
export function swipeToDelete(
  content: HTMLElement,
  { onDelete, threshold = 96, actionLabel = 'Löschen' }: SwipeOptions,
): HTMLElement {
  const background = el('div', { class: 'swipe-bg', 'aria-hidden': 'true' }, [
    icon('trash', { size: 18 }),
    el('span', {}, actionLabel),
  ]);

  const foreground = el('div', { class: 'swipe-fg' }, content);
  const wrap = el('div', { class: 'swipe-wrap' }, [background, foreground]);

  let startX = 0;
  let startY = 0;
  let dx = 0;
  let tracking = false;
  let decided: 'horizontal' | 'vertical' | null = null;
  let armed = false;

  foreground.addEventListener(
    'pointerdown',
    (e: PointerEvent) => {
      // Nur direkte Zeigergesten – nicht, wenn gerade ein Knopf gedrückt wird.
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      tracking = true;
      decided = null;
      armed = false;
      startX = e.clientX;
      startY = e.clientY;
      dx = 0;
      foreground.style.transition = 'none';
    },
    { passive: true },
  );

  foreground.addEventListener(
    'pointermove',
    (e: PointerEvent) => {
      if (!tracking) return;
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      // Richtung einmalig festlegen: senkrechtes Scrollen darf nicht kapern.
      if (!decided) {
        if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
        decided = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
        if (decided === 'horizontal') foreground.setPointerCapture(e.pointerId);
      }
      if (decided !== 'horizontal') return;

      // Nur nach links, mit Gummiband am Ende.
      dx = Math.min(0, deltaX);
      const shown = Math.abs(dx) > threshold ? -threshold - (Math.abs(dx) - threshold) * 0.3 : dx;
      foreground.style.transform = `translateX(${shown}px)`;

      const nowArmed = Math.abs(dx) >= threshold;
      if (nowArmed !== armed) {
        armed = nowArmed;
        if (armed) haptic(10);
      }
    },
    { passive: true },
  );

  const finish = () => {
    if (!tracking) return;
    tracking = false;
    foreground.style.transition = '';

    if (decided === 'horizontal' && Math.abs(dx) >= threshold) {
      foreground.style.transform = 'translateX(-100%)';
      wrap.style.transition = 'height 200ms, opacity 200ms, margin 200ms';
      window.setTimeout(onDelete, 160);
      return;
    }
    foreground.style.transform = '';
  };

  foreground.addEventListener('pointerup', finish);
  foreground.addEventListener('pointercancel', finish);

  return wrap;
}
